import {
  Alert,
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormHelperText,
  Text,
  Link,
  HStack,
  Select,
  Stack,
  useToast,
  useDisclosure,
} from '@chakra-ui/react'
import * as global from 'brokertslib/src/helpers/global'
import { DiscountType, Error } from 'BrokerTsLib/src/dtos'
import { Insurer, SelectionType } from 'BrokerTsLib/src/enums'
import { useContext, useEffect, useState } from 'react'
import { Controller, FormProvider, useForm, useFieldArray } from 'react-hook-form'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import { FormGroup } from '../components/FormGroup'

import { FormLabel } from '../components/FormLabel'
import {
  FormSection,
  FormSectionBody,
  FormSectionHeader,
  FormSectionTitle,
} from '../components/FormSection'
import { InputGroup } from '../components/InputGroup'
import { MainContent } from '../components/MainContent'
import { Radio } from '../components/Radio'
import { RadioGroup as ChakraRadioGroup, Radio as ChakraRadio } from '@chakra-ui/react'
import { RadioStack } from '../components/RadioStack'
import { TextareaGroup } from '../components/TextareaGroup'
import { useSaveMeetingRecord, useCreateContract } from '../hooks/useWizard'
import { Check, Duplicate } from '../icons'
import { FormButtons } from './FormButtons'
import { baseUrl, useSelections } from '../hooks'
import { useSendContractEmail } from '../hooks/useOrigApp'
import { AxiosError } from 'axios'
import { Loading } from '../components/Loading'
import { insuranceParamsContext } from '../layouts/Wizard'
import { meetingRecordContext } from '../pages/carkulka/wizard/5'
import ErrorSummary from '../components/ErrorSummary'
import { useCloneInsuranceParams, useGetAvailableBusinessmen } from '../hooks/useInsuranceParams'
import SubmitDialog from 'BrokerTsLib/src/components/SubmitDialog'
import { useAuth } from '../context/AuthContext'
import { useStorePdfCalculation, useGetDocuments } from '../hooks/useDocuments'
import { OkAppsMeetingRecord } from '../dtos/OkAppsMeetingRecord'
import { insurerOfferContext } from './hoc/WithOffer'

const insuranceParamsApiUrl = process.env.REACT_APP_INSURANCEPARAMSTRAVEL_API
const insuranceParamsUrl = `${baseUrl}/${insuranceParamsApiUrl}`

export const MeetingRecordFormTravel = () => {
  const navigate = useNavigate()
  const { user } = useAuth() ?? {}
  const { insuranceParamsId, insuranceParams, refetchInsuranceParams } =
    useContext(insuranceParamsContext)
  const [acceptChangedPrice, setAcceptChangedPrice] = useState(false)
  const [priceChanged, setPriceChanged] = useState(false)
  const [returnBack, setReturnBack] = useState(true)
  const [alerts, setAlerts] = useState<Error[]>([])
  const [isSubmittingCurrent, setIsSubmittingCurrent] = useState(false)
  const [isSubmittingNext, setIsSubmittingNext] = useState(false)
  const { mutateAsync: sendContractEmail } = useSendContractEmail()

  const {
    isLoading: isLoadingMR,
    refetch: refetchMR,
    meetingRecord,
  } = useContext(meetingRecordContext)

  const { offer, refetch: refetchOffer } = useContext(insurerOfferContext)
  // const { refetch: refetchDocuments } = useGetDocuments(insuranceParams?.generalParams.selectedOfferId!, { enabled: false })
  const toast = useToast()

  const { refetch: refetchDocuments } = useGetDocuments(
    insuranceParams?.generalParams.selectedOfferId!,
    { enabled: false },
  )

  const { mutateAsync: cloneInsuranceParams } = useCloneInsuranceParams(insuranceParamsId!)

  const allowCreateContract = (user?.insuringAllowed ?? 0) == 1

  const { data: dataBusinessmen } = useGetAvailableBusinessmen(insuranceParamsId!)

  // save formulare
  const { mutateAsync: saveMeetingRecord } = useSaveMeetingRecord()

  // sjednani smlouvy
  const { mutateAsync: createContract } = useCreateContract()

  // reset formulare po nacteni dat
  useEffect(() => {
    if (!!meetingRecord) reset(meetingRecord)
  }, [!meetingRecord])

  const methods = useForm<OkAppsMeetingRecord>({
    defaultValues: meetingRecord,
    mode: 'onBlur',
  })

  const {
    control,
    handleSubmit,
    register,
    getValues,
    setValue,
    watch,
    reset,
    setError,
    formState: { errors, isSubmitting },
    getFieldState,
  } = methods

  const isValid = !Object.keys(errors).length

  const promiseSendContractEmail = (
    doSendContractEmail: boolean,
    paramsId: string,
    insuranceId?: number | null,
  ) => {
    if (!doSendContractEmail) return null

    return sendContractEmail({ id: paramsId, insuranceId: insuranceId })
  }

  const { mutateAsync: promiseStoreCalculationPdf } = useStorePdfCalculation(insuranceParamsId!, {
    enabled: false,
  })

  useEffect(() => {
    if (!isSubmitting) {
      setIsSubmittingCurrent(false)
      setIsSubmittingNext(false)
    }
  }, [isSubmitting])

  const { data: dataCat } = useSelections(SelectionType.FormaSjednani)
  const { fields } = useFieldArray({ control, name: 'brokerNumbers' })

  // zobrazeni validace nabidky ve validation summary (zobrazuje se uz pri getu)
  useEffect(() => {
    if (meetingRecord?.errors)
      meetingRecord.errors.forEach((x) => setError('' as any, { message: x.message }))
  }, [meetingRecord?.errors])

  const {
    isOpen: isSavedFormAlertOpen,
    onOpen: savedFormAlertOnOpen,
    onClose: savedFormAlertOnClose,
  } = useDisclosure()

  const onSubmit = (values: OkAppsMeetingRecord) => {
    return new Promise((resolve, reject) => {
      if (!values.hasMoreContracts) values.hasMoreContracts = false // pokud se nezmění výchozí hodnota (undefined), je třeba nastavit na false
      if (!values.refusesMoreDetails) values.refusesMoreDetails = false // pokud se nezmění výchozí hodnota (undefined), je třeba nastavit na false

      return saveMeetingRecord(values)
        .then((resp) => {
          // v pripade pouze ulozeni jenom refreshneme formular
          if (values.nextPage == 'current') {
            savedFormAlertOnOpen()
            refetchMR()
            window.scrollTo(0, 0)

            // pokud nesjednavam pojistku, tak nepokracuji
            resolve(resp)
            return
          }

          // sjednani pojistky
          createContract({ paramsId: insuranceParamsId!, acceptChangedPrice: acceptChangedPrice })
            .then((resp) => {
              resolve(resp)

              if (resp.data.priceChanged && !resp.data.contractCreated) {
                setPriceChanged(true)
                setReturnBack(resp.data.returnBack)
                if (resp.data.errors.length > 0) setAlerts(resp.data.errors)
              } else if (resp.data.errors.length > 0) setAlerts(resp.data.errors)
              else {
                refetchInsuranceParams()
                refetchMR()
                refetchOffer()

                window.scrollTo(0, 0)

                const promises = []
                promises.push(
                  promiseSendContractEmail(
                    resp.data.sendContractEmail,
                    insuranceParamsId!,
                    insuranceParams?.generalParams.insuranceId,
                  ),
                )
                if (resp.data.storeCalculationPdf) promises.push(promiseStoreCalculationPdf())

                Promise.all(promises)
                  .then(([, resultStorePdfCalc]) => {
                    let toastText = ''
                    let showToast = false

                    if (resultStorePdfCalc && resultStorePdfCalc.data.success) {
                      refetchDocuments()
                    }
                    if (
                      resultStorePdfCalc &&
                      !resultStorePdfCalc.data.success &&
                      resultStorePdfCalc.data.text
                    ) {
                      toastText = toastText + ' ' + resultStorePdfCalc.data.text
                      showToast = true
                    }

                    if (showToast) {
                      toast({
                        title: toastText,
                        status: 'error',
                        duration: 60000,
                        isClosable: true,
                      })
                    }
                  })
                  .catch((err: AxiosError) => {
                    reject(err)
                    if (err.response && err.response.status === 500 && err.response.data)
                      toast({
                        title: err.response.data as string,
                        status: 'error',
                        duration: 60000,
                        isClosable: true,
                      })
                  })
              }
            })
            .catch((err: AxiosError) => {
              reject(err)
            })
        })
        .catch((err) => {
          reject(err)
        })
    })
  }

  const submitForm = handleSubmit((values) => {
    return onSubmit(values).catch((err) => {
      if (!(err.response && err.response.status === 400 && err.response.data)) return

      let data = err.response.data

      // zobrazeni serverove validace
      Object.keys(data).forEach((x: any) => {
        setError(global.toCamelCase(x), { message: data[x].join(' ') })
      })
    })
  })

  useEffect(() => {
    if (isSubmittingNext || isSubmittingCurrent) submitForm()
  }, [isSubmittingNext, isSubmittingCurrent])

  const reasonTexts: { [key: string]: string } = {
    text1: 'Rozsah pojistného krytí s ohledem na výši pojistného.',
    text2: 'Cena, rozsah pojištění a limity/pojistná částka odpovídají požadavkům Zákazníka.',
    text3:
      'Z nabízených variant si Zákazník sám zvolil tuto pojišťovnu a trvá na uzavření tohoto pojištění.',
  }

  const consequenceTexts: { [key: string]: string } = {
    text1:
      'V případě vzniku škodní události nebo při podezření na vznik škodní události bezodkladně kontaktovat asistenční službu pojistitele.',
    text2: 'Pojistné plnění je omezeno limity pojistného plnění uvedenými v pojistné smlouvě.',
    text3:
      'Pojištění se nevztahuje na újmy v souvislosti s cestou do oblasti, která byla státem označena jako nebezpečná, nebo bylo doporučeno do dané oblasti necestovat.',
    text4:
      'Vznik smlouvy je podmíněn zaplacením pojistného, které musí být připsáno na účtu pojišťovny dle platebních instrukcí uvedených v návrhu smlouvy.',
    text5:
      'Pojištění se nevztahuje na újmy způsobené válečnými událostmi, zásahem státní moci nebo veřejné správy.',
  }

  const pcn = getValues('previousContractNumber')
  const contractArrangedRemotelyEnabled = getValues('contractArrangedRemotelyEnabled')
  const actualConsequences = watch('consequences')

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <MainContent>
          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Záznam z jednání</FormSectionTitle>
            </FormSectionHeader>

            {!meetingRecord && isLoadingMR && <Loading large={true} />}
            {meetingRecord && (
              <FormSectionBody sx={{ gap: 8 }}>
                <FormControl variant="fullWidth">
                  <FormLabel
                    optional
                    tooltip="Uveďte nesrovnalosti mezi nabízeným pojištěním a požadavky zákazníka. Tyto údaje se propíšou do Záznamu z jednání, který zákazník podepisuje."
                  >
                    Případné nesrovnalosti mezi požadavky Zákazníka a sjednávaným pojištěním nebo
                    jeho podstatnou změnou
                  </FormLabel>

                  <TextareaGroup
                    placeholder="Uveďte nesrovnalosti mezi nabízeným pojištěním a požadavky zákazníka. Tyto údaje se propíšou do Záznamu z jednání, které zákazník podepisuje."
                    errors={errors.irregularities}
                    {...register('irregularities')}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel tooltip="Po vyhodnocení výše uvedených požadavků a potřeb Zákazníka v požadovaných oblastech pojištění a při zohlednění jeho znalostí, zkušeností a finanční situace doporučuje Zprostředkovatel následující produkty. Toto doporučení je založeno pouze na vyhodnocení produktů, které je Zprostředkovatel a jeho zástupce oprávněn zprostředkovávat. Můžete vybrat předpřipravenou textaci důvodu, po vybrání můžete doplnit vlastní text.">
                    Důvody, na kterých je doporučení založeno
                  </FormLabel>
                  <Stack>
                    <CheckboxGroup>
                      <Stack gap={2}>
                        {Object.keys(reasonTexts).map((key) => (
                          <Checkbox
                            key={key}
                            value={key}
                            onChange={(e) => {
                              setValue(
                                'reasons',
                                e.target.checked
                                  ? `${getValues('reasons')} ${reasonTexts[key]}`.trim()
                                  : getValues('reasons')?.replace(reasonTexts[key], '').trim(),
                              )
                            }}
                          >
                            <Text lineHeight="5">{reasonTexts[key]}</Text>
                          </Checkbox>
                        ))}
                      </Stack>
                    </CheckboxGroup>
                  </Stack>
                </FormControl>

                <FormControl variant="fullWidth" isInvalid={!!errors.reasons?.message} isRequired>
                  <TextareaGroup
                    placeholder="Důvody, na kterých je doporučení založeno"
                    errors={errors.reasons}
                    {...register('reasons')}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel tooltip="V případě doporučení sjednání nové pojistné smlouvy nebo její podstatné změny je třeba vysvětlit dopady takového jednání vč. souvisejících rizik u každého sjednaného pojistného produktu.">
                    Popis dopadů sjednání/změny/ukončení pojištění, včetně souvisejících rizik
                  </FormLabel>
                  <Stack>
                    <CheckboxGroup>
                      <Stack gap={2}>
                        {Object.keys(consequenceTexts).map((key) => (
                          <Checkbox
                            key={key}
                            value={key}
                            onChange={(e) => {
                              setValue(
                                'consequences',
                                e.target.checked
                                  ? `${getValues('consequences')} ${consequenceTexts[key]}`.trim()
                                  : getValues('consequences')
                                      ?.replace(consequenceTexts[key], '')
                                      .trim(),
                              )
                            }}
                          >
                            <Text lineHeight="5">{consequenceTexts[key]}</Text>
                          </Checkbox>
                        ))}
                      </Stack>
                    </CheckboxGroup>
                  </Stack>
                </FormControl>

                <FormControl
                  variant="fullWidth"
                  isInvalid={!!errors.consequences?.message}
                  isRequired
                >
                  <TextareaGroup
                    placeholder="Napište popis dopadů..."
                    errors={errors.consequences}
                    {...register('consequences')}
                  />

                  <FormHelperText>
                    V případě doporučení sjednání nové pojistné smlouvy nebo její podstatné změny je
                    třeba vysvětlit dopady takového jednání vč. souvisejících rizik u každého
                    sjednaného pojistného produktu.
                  </FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel tooltip="V souvislosti s tím Zprostředkovatel upozorňuje Zákazníka, že byl případnou chybějící informací ovlivněn při doporučení pojistného produktu a nastavení vhodných pojistných částek/limitů poj. plnění u požadovaných nebezpečí a výše pojistného.">
                    Zákazník odmítá sdělit další požadavky?
                  </FormLabel>

                  <Controller
                    name="refusesMoreDetails"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <ChakraRadioGroup
                        onChange={(e) => onChange(e == '1')}
                        value={value ? '1' : '0'}
                      >
                        <RadioStack variant="stackedTile">
                          <ChakraRadio value={'1'}>Ano</ChakraRadio>
                          <ChakraRadio value={'0'}>Ne</ChakraRadio>
                        </RadioStack>
                      </ChakraRadioGroup>
                    )}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel tooltip="Má zákazník vedle požadované/měněné pojistné smlouvy uzavřenou další pojistnou smlouvu, která kryje stejné pojistné nebezpečí?">
                    Má zákazník vedle této pojistné smlouvy uzavřenou další se stejným krytím?
                  </FormLabel>

                  <Controller
                    name="hasMoreContracts"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <ChakraRadioGroup
                        onChange={(e) => onChange(e == '1')}
                        value={value ? '1' : '0'}
                      >
                        <RadioStack variant="stackedTile">
                          <ChakraRadio value={'1'}>Ano</ChakraRadio>
                          <ChakraRadio value={'0'}>Ne</ChakraRadio>
                        </RadioStack>
                      </ChakraRadioGroup>
                    )}
                  />
                </FormControl>

                {/* <FormControl>
                  <FormLabel tooltip="Jaké má zákazník znalosti produktů?">
                    Jaké má zákazník znalosti produktů?
                  </FormLabel>

                  <Controller
                    name="customerKnowledge"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <ChakraRadioGroup onChange={e => onChange(parseInt(e))} value={value?.toString() ?? ''}>
                        <RadioStack variant='simple' sx={{ gridAutoFlow: 'row' }}>
                          {AvailableCustomerKnowledge.map(item => <ChakraRadio key={item.id} value={item.id.toString()}>{item.name}</ChakraRadio>)}
                        </RadioStack>
                      </ChakraRadioGroup>
                    )}
                  />
                </FormControl> */}

                <Controller
                  name="noCollison"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <CheckboxGroup
                      onChange={(e) => onChange(e[0] == '1')}
                      value={value ? ['1'] : undefined}
                    >
                      <Checkbox value={'1'}>
                        Zákazník ani Zprostředkovatel si nejsou vědomi žádných nesrovnalostí mezi
                        požadavky Zákazníka a nabízeným pojištěním.
                      </Checkbox>
                    </CheckboxGroup>
                  )}
                />

                <FormControl isInvalid={!!errors.placeOfContract?.message} isRequired>
                  <FormLabel>Místo sjednání</FormLabel>
                  <InputGroup
                    placeholder="Např. Brno"
                    {...register('placeOfContract')}
                    errors={errors.placeOfContract}
                  />
                </FormControl>

                {offer?.insurerId == Insurer.Allianz &&
                  meetingRecord.discounts
                    ?.filter(
                      (x) =>
                        x.insuranceCategoryDiscount.discountType == DiscountType.Voucher &&
                        (x.discount ?? 0) > 0,
                    )
                    .map((x, i) => (
                      <FormControl
                        isInvalid={!!getFieldState(`discounts.${i}.voucherCode` as any).error}
                        key={x.insuranceCategoryDiscountId}
                      >
                        <FormLabel>
                          {x.insuranceCategoryDiscount.name} ({x.discount}%)
                        </FormLabel>
                        <InputGroup
                          {...register(`discounts[${i}].voucherCode` as any)}
                          errors={getFieldState(`discounts.${i}.voucherCode` as any).error}
                        />
                      </FormControl>
                    ))}

                <FormGroup>
                  <FormControl>
                    <FormLabel tooltip="Druh smlouvy">Druh smlouvy</FormLabel>
                    <ChakraRadioGroup>
                      <RadioStack variant="stackedTile">
                        <Radio isDisabled isChecked={!pcn}>
                          Nová smlouva
                        </Radio>
                        <Radio isDisabled isChecked={!!pcn}>
                          Náhrada stávající smlouvy
                        </Radio>
                      </RadioStack>
                    </ChakraRadioGroup>
                  </FormControl>

                  {!!pcn && (
                    <FormControl>
                      <FormLabel>Číslo stávající smlouvy</FormLabel>
                      <InputGroup isDisabled {...register('previousContractNumber')} />
                    </FormControl>
                  )}
                </FormGroup>

                {contractArrangedRemotelyEnabled && (
                  <FormGroup>
                    <FormControl isRequired>
                      <FormLabel>Smlouva je sjednána</FormLabel>

                      <Controller
                        name="isContractArrangedRemotely"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <ChakraRadioGroup
                            onChange={(e) => onChange(e == '1')}
                            value={value ? '1' : '0'}
                          >
                            <RadioStack variant="stackedTile">
                              <ChakraRadio value={'0'}>Osobně</ChakraRadio>
                              <ChakraRadio value={'1'}>Na dálku</ChakraRadio>
                            </RadioStack>
                          </ChakraRadioGroup>
                        )}
                      />
                    </FormControl>
                  </FormGroup>
                )}

                {dataBusinessmen?.data &&
                  dataBusinessmen?.data.length > 0 &&
                  user!.allowCreateInsuranceAsSomeoneElse && (
                    <FormControl>
                      <FormLabel tooltip="Slouží pro propsání klienta a smlouvy pod jiného získatele/správce dle výběru níže. V případě, že chcete doplnit další osobu obraťte se s žádostí na carkulka@okholding.cz">
                        Sjednání za jiného uživatele
                      </FormLabel>

                      <Box sx={{ display: 'grid', gap: 4 }}>
                        <HStack>
                          <FormLabel tooltip="Klient bude zapsán pod vybraným získatelem. V případě, že tyto hodnoty měnit nechcete, ponechejte pole prázdné.">
                            Ziskatel
                          </FormLabel>
                          <Select
                            id="businessmanId"
                            {...register('businessmanId')}
                            defaultValue={getValues('businessmanId') ?? ''}
                          >
                            <option value="">Vyberte ziskatele</option>

                            {dataBusinessmen.data.map((x) => (
                              <option value={x.id} key={x.id}>
                                {x.fullname}
                              </option>
                            ))}
                          </Select>
                        </HStack>

                        <HStack>
                          <FormLabel tooltip="Smlouva bude zapsaná pod vybraného správce. V případě, že tyto hodnoty měnit nechcete, ponechejte pole prázdné.">
                            Správce
                          </FormLabel>
                          <Select
                            id="businessmanId"
                            {...register('managerId')}
                            defaultValue={getValues('managerId') ?? ''}
                          >
                            <option value="">Vyberte správce</option>

                            {dataBusinessmen.data.map((x) => (
                              <option value={x.id} key={x.id}>
                                {x.fullname}
                              </option>
                            ))}
                          </Select>
                        </HStack>
                      </Box>
                    </FormControl>
                  )}

                <FormControl>
                  <FormLabel>Získatelské číslo</FormLabel>
                  {fields.map((field, index) => (
                    <InputGroup
                      key={field.acquisitionNumber}
                      placeholder="Např. 12348765"
                      {...register(`brokerNumbers.${index}.acquisitionNumber`)}
                      isDisabled
                    />
                  ))}
                </FormControl>
              </FormSectionBody>
            )}
          </FormSection>

          {(priceChanged || alerts.length > 0) && (
            <Alert colorScheme="red" mb={8}>
              <Stack direction="column">
                {priceChanged && (
                  <Text>
                    Před sjednáním došlo ke změně ceny.
                    {returnBack && (
                      <>
                        {' '}
                        Vraťte se na{' '}
                        <Link
                          as={RouterLink}
                          colorScheme="blue"
                          to={`/carkulka/wizard/3/${insuranceParamsId!}`}
                        >
                          &nbsp;seznam kalkulací&nbsp;
                        </Link>{' '}
                        a vybranou nabídku překalkulujte.
                      </>
                    )}
                  </Text>
                )}

                {alerts.length > 0 &&
                  alerts
                    .filter((x) => !!x.message)
                    .map((alert) => (
                      <Text
                        key={alert.message}
                        dangerouslySetInnerHTML={{ __html: alert.message ?? '' }}
                      />
                    ))}
              </Stack>
            </Alert>
          )}

          <ErrorSummary errors={errors} />

          {/* {meetingRecord?.errors && meetingRecord.errors.length > 0 &&
            <Alert colorScheme='red' mb={8} justifyContent='end'>
              <VStack >
                {meetingRecord.errors.map(x => <Text>{x.message}</Text>)}
              </VStack>
            </Alert>
          } */}

          <FormButtons
            leftButtons={
              <Button
                variant="outline"
                as="a"
                href={`${insuranceParamsUrl}/CloneUrl?paramsId=${insuranceParamsId}&replacement=${false}&returnUrl=${encodeURIComponent(`${baseUrl}/cestovko/wizard/1`)}`}
                disabled={isSubmitting}
              >
                <Duplicate color="secondary.500" />
                Klonovat
              </Button>
            }
            rightButtons={
              <>
                <Button
                  variant="outline"
                  disabled={isSubmitting || isSubmittingNext || isSubmittingCurrent || !isValid}
                  isLoading={isSubmittingCurrent && isValid}
                  type="button"
                  onClick={() => {
                    setValue('nextPage', 'current')
                    setIsSubmittingCurrent(true)
                  }}
                >
                  Uložit
                </Button>

                {allowCreateContract && (
                  <Button
                    colorScheme="green"
                    disabled={isSubmitting || isSubmittingNext || isSubmittingCurrent || !isValid}
                    isLoading={isSubmittingNext && isValid}
                    loadingText="Sjednávám..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'next')
                      setIsSubmittingNext(true)
                      setAcceptChangedPrice(priceChanged)
                    }}
                  >
                    <Check />
                    {!priceChanged ? 'Sjednat pojištění' : 'Sjednat pojištění se změnou ceny'}
                  </Button>
                )}

                <SubmitDialog
                  isOpen={isSavedFormAlertOpen}
                  onClose={savedFormAlertOnClose}
                  header="Uloženo"
                  body={'Údaje byly úspěšně uloženy'}
                />
              </>
            }
          />
        </MainContent>
      </form>
    </FormProvider>
  )
}
